import React, { useContext, useEffect, useState } from 'react';

import { Place, PlaceFilters } from '../../app/models/Place';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import usePlaceService from '../../app/services/hooks/usePlaceService';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import PlaceCreate from './components/PlaceCreate';
import PlaceEdit from './components/PlaceEdit';
import PlaceDataTable from './components/PlaceDataTable';
import { AppContext } from '../../contexts/AppContext';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { useHistory } from 'react-router-dom';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Origenes y destinos',
        url: '/Places',
        isActive: true
    }
];

const Places = () => {
    const history = useHistory();
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { fetchingGetPlaces, getPlaces, deletePlace, activePlace } = usePlaceService();
    const [places, setPlaces] = useState<Place[]>([]);
    const { navigationPage } = useNavigationPage();

    const [placeIdEdit, setPlaceIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [placeFilter, setPlaceFilter] = useState<PlaceFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        getAllPlaces();
    }, [placeFilter.page, placeFilter.per_page, placeFilter.sort, placeFilter.order]);

    const getAllPlaces = () => {
        getPlaces(placeFilter, {
            onSuccess: (response: ServiceResponse) => {
                setPlaces(response.data.places);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message, {
                    autoClose: 2000
                });

                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (PlaceId: number) => {
        setShowingEdit(true);
        setPlaceIdEdit(PlaceId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setPlaceIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllPlaces();
    };

    const destroy = (placeId: number) => {
        const _text = 'Está a punto de eliminar el origen/destino #' + placeId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando origen/destino...');
                        deletePlace(placeId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El origen/destino no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const active = (afpId: number, active: boolean) => {
        activePlace(afpId, active, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                reloadTable();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Origenes y destinos" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nuevo origen/destino" />}
                />
                <PlaceDataTable
                    places={places}
                    edit={showEdit}
                    destroy={destroy}
                    active={active}
                    loading={fetchingGetPlaces}
                    filter={placeFilter}
                    setFilter={setPlaceFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                    onSearch={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear origen/destino"
                    backdrop={true}
                    showFooter={false}
                >
                    <PlaceCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Origen/Destino"
                    backdrop={true}
                    showFooter={false}
                >
                    <PlaceEdit
                        placeId={placeIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default Places;
